/* eslint-disable array-bracket-newline */
/**
 * Implements a limit proxy modal
 * Allows to withdrawal coins
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Address, useAccount, useNetwork } from 'wagmi';
import {
  prepareWriteContract,
  readContract,
  waitForTransaction,
  writeContract,
} from 'wagmi/actions';
import { Network } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import useDevice from '../../../pages/MainPage/useMobileDetect';
import { KattanaAPI } from '../../../store/createStore';
import Loader from '../../common/Loader';
import TokensTableMobile from './mobile/TokensTable';
import StatsTable from './StatsTable';
import TokensTable from './TokensTable';
import { onCopyAddress, onWalletScan } from '../../../helpers/addressUtils';
import { proxyAbi, userWalletFactoryAbi } from '../../../helpers/abis';
import { isNative } from '../../Trading/helpers/nativeCoins';
import { actionCreateNotification } from '../../../actions/notifications';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import {
  ContractRoles,
  VOID_ADDRESS,
  getLimitDeployAddress,
} from '../../Trading/helpers/limitOrderRoles';
import { formatAddress } from '../../../helpers/formatters';

enum Stage {
  OK,
  LOADING,
  NO_DATA,
  NO_WALLET,
  NO_PROXY,
}

interface ILimitProxyModalProps {
  onCancel: () => void;
}

type ProxyAddress = Address | undefined;

/**
 * Return address of proxy wallet or NO_ADDR
 * @param lp - proxy class
 * @param address - string of wallet
 * @returns
 */
const getProxyAddress = async (chainId: number, account: string) => {
  try {
    console.log('limit modal: checking if wallet has proxy');
    const address = getLimitDeployAddress(chainId, ContractRoles.WalletFactory);
    if (!address) {
      return undefined;
    }
    const proxyAddress = await readContract({
      address,
      abi: userWalletFactoryAbi,
      functionName: 'getUserWallet',
      args: [account as Address],
    });
    if (proxyAddress !== VOID_ADDRESS) {
      return proxyAddress;
    } else {
      return undefined;
    }
  } catch (e) {
    console.warn(e);
    return undefined;
  }
};

/**
 * Return list of proxy tokens
 * @param id - id of chain
 * @param address - proxy
 */
const getProxyTokens = async (id: number, address: string) => {
  try {
    const tokens = await KattanaAPI.balances(id, address);
    return {
      tokens,
      ok: true,
    };
  } catch (e) {
    console.warn(e);
    return {
      tokens: [],
      ok: false,
    };
  }
};

const LimitProxyModal = ({ onCancel }: ILimitProxyModalProps) => {
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { isMobile } = useDevice();
  const [proxyAddr, setProxyAddr] = useState<ProxyAddress>();
  const [tokens, setTokens] = useState<any>([]);
  const [stage, setStage] = useState(Stage.LOADING);

  const dispatch = useDispatch();

  /**
   * Stage 1: Check if user has any wallet
   */
  useEffect(() => {
    setProxyAddr(undefined);
    if (address && chain) {
      setStage(Stage.LOADING);
      const chId = chain.id;
      getProxyAddress(chId, address).then((proxy) => {
        if (proxy) {
          setProxyAddr(proxy);
        } else {
          setStage(Stage.NO_PROXY);
        }
      });
    } else {
      setStage(Stage.NO_WALLET);
    }
  }, [chain, address]);

  /**
   * Stage 2: Fetch coins
   */
  useEffect(() => {
    if (proxyAddr && chain) {
      const chId = chain.id;
      getProxyTokens(chId, proxyAddr).then((resp) => {
        const { ok, tokens } = resp;
        if (ok) {
          setStage(Stage.OK);
          setTokens(tokens || []);
        } else {
          setStage(Stage.NO_DATA);
          setTokens([]);
        }
      });
    }
  }, [proxyAddr, chain]);

  const networkSymbol = Network.fromChainId(chain?.id || 1)?.getSymbol() || '';

  const onWithdraw = async (
    token: Address,
    decimals: number,
    balance: string
  ) => {
    try {
      if (!proxyAddr) {
        return;
      }
      const native = isNative(token);
      const data = native
        ? [address, BigInt(balance)]
        : [token, address, BigInt(balance)];
      const { request } = await prepareWriteContract({
        address: proxyAddr,
        abi: proxyAbi,
        functionName: native ? 'demandETH' : 'demandERC20',
        args: data as any,
      });
      const demandTx = await writeContract(request);
      if (chain) {
        dispatch(
          actionAppendTransaction(
            toIntermediateTransaction(demandTx.hash, chain.id)
          )
        );
        const demandTxReceipt = await waitForTransaction(demandTx);
        console.log(demandTxReceipt);

        if (demandTxReceipt.status === 'success') {
          const resp = await getProxyTokens(chain.id, proxyAddr);
          const { ok, tokens } = resp;
          if (ok) {
            setStage(Stage.OK);
            setTokens(tokens);
          } else {
            setStage(Stage.NO_DATA);
            setTokens([]);
          }
        }
      }
    } catch (error: any) {
      dispatch(
        actionCreateNotification({
          kind: 'error',
          message: error?.message || error?.message,
        })
      );
    }
  };

  const renderContent = () => {
    switch (stage) {
    case Stage.LOADING:
      return (
        <div className='wallet_modal__container__loader'>
          <Loader />
        </div>
      );
    case Stage.NO_DATA:
      return (
        <Text size={14} className='centrify text--grey-400 text--weight-500'>
          Could not load tokens data for proxy contract
        </Text>
      );
    case Stage.NO_PROXY:
      return (
        <Text size={14} className='centrify text--grey-400 text--weight-500'>
          Please deploy a proxy contract to see statistics
        </Text>
      );
    case Stage.NO_WALLET:
      return (
        <Text size={14} className='centrify text--grey-400 text--weight-500'>
          Please connect wallet to interact with proxy contract
        </Text>
      );
    case Stage.OK:
      return (
        <>
          <StatsTable tokens={tokens} />
          {isMobile ? (
            <TokensTableMobile
              network={Network.fromChainId(chain?.id || 1)}
              tokens={tokens}
              onWithdraw={onWithdraw}
            />
          ) : (
            <div className='trades_table table_desktop__container'>
              <TokensTable
                network={Network.fromChainId(chain?.id || 1)}
                tokens={tokens}
                onWithdraw={onWithdraw}
              />
            </div>
          )}
        </>
      );
    default:
      return null;
    }
  };

  return (
    <div className='wallet_modal'>
      <div className='wallet_modal__header'>
        <span>
          <div className='wallet_modal__wallet__title'>
            {isMobile ? formatAddress(proxyAddr || '') : proxyAddr}
          </div>
          &nbsp;on&nbsp;
          <span className='wallet_modal__wallet__pair_name'>{chain?.name}</span>
        </span>
        <div className='wallet_modal__header__actions'>
          <div
            className='wallet_modal__pill wallet_modal__secondary icon-copy'
            title='Copy address'
            onClick={() => onCopyAddress(proxyAddr || '')}
          />
          <div
            className='wallet_modal__pill wallet_modal__secondary icon-top-right-square'
            title='Open scan'
            onClick={() => onWalletScan(proxyAddr || '', networkSymbol)}
          />
        </div>
      </div>
      <div
        className='wallet_modal__close'
        onClick={onCancel}
      />
      <div className='wallet_modal__container'>{renderContent()}</div>
    </div>
  );
};

export default LimitProxyModal;
